import React from 'react';
import type { ProgramWorkout } from '../types';

interface WorkoutOverviewProps {
  workout: ProgramWorkout;
  onStartExercise: (exerciseIndex: number) => void;
  onCancel: () => void;
}

/**
 * WorkoutOverview - Shows workout details and allows user to choose starting exercise
 */
export const WorkoutOverview: React.FC<WorkoutOverviewProps> = ({
  workout,
  onStartExercise,
  onCancel
}) => {
  const totalExercises = workout.exercises.length;
  const estimatedDuration = workout.exercises.reduce((total, ex) => {
    const sets = ex.target_sets || 3;
    const restTime = (ex.rest_seconds || 60) * sets;
    const workTime = sets * 30; // Assume ~30 seconds per set
    return total + (restTime + workTime) / 60;
  }, 0);

  return (
    <div className="workout-overview p-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{workout.name}</h1>
          <p className="text-sm text-gray-600">
            {totalExercises} упражнений • ~{Math.round(estimatedDuration)} мин
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 p-2"
          title="Назад"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Exercise List */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Упражнения на сегодня:</h2>
        <div className="space-y-3">
          {workout.exercises.map((exercise, index) => (
            <button
              key={exercise.id}
              onClick={() => onStartExercise(index)}
              className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-lg font-medium text-gray-700">
                      {index + 1}.
                    </span>
                    <h3 className="text-lg font-medium">
                      {exercise.exercise.name_ru}
                    </h3>
                  </div>
                  
                  {/* Target sets and reps */}
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                    {exercise.target_sets && (
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {exercise.target_sets} подходов
                      </span>
                    )}
                    {(exercise.target_reps_min || exercise.target_reps_max) && (
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {exercise.target_reps_min || '?'}-{exercise.target_reps_max || '?'} повторов
                      </span>
                    )}
                    {exercise.rest_seconds && (
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {Math.floor(exercise.rest_seconds / 60)}:{(exercise.rest_seconds % 60).toString().padStart(2, '0')} отдых
                      </span>
                    )}
                  </div>

                  {/* Muscle groups and equipment */}
                  <div className="flex flex-wrap gap-2">
                    {(exercise.exercise as any).muscle_group && (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {(exercise.exercise as any).muscle_group}
                      </span>
                    )}
                    {(exercise.exercise as any).equipment && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {(exercise.exercise as any).equipment}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow icon */}
                <svg className="w-6 h-6 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Start Button */}
      <div className="sticky bottom-4">
        <button
          onClick={() => onStartExercise(0)}
          className="w-full bg-green-600 text-white py-4 px-6 rounded-lg font-medium text-lg hover:bg-green-700 shadow-lg"
        >
          Начать с первого упражнения
        </button>
      </div>
    </div>
  );
};

export default WorkoutOverview;
